/**
 * Game invitations via TanStack Query (docs/APP_PLAN.md §5.2, §12 M5;
 * APP_SPEC §20), following the same pattern as `lib/friends.ts`. This is
 * the only place `apps/web` talks to `/api/invitations/*`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { GameSession, Invitation } from '@perplexicon/api-client';

import { apiClient } from './api-client.js';
import { GAME_SESSIONS_LIST_QUERY_KEY, gameSessionQueryKey } from '../features/games/query-keys.js';

export const INVITATIONS_INCOMING_QUERY_KEY = ['invitations', 'incoming'] as const;
export const INVITATIONS_OUTGOING_QUERY_KEY = ['invitations', 'outgoing'] as const;

/** Polling interval so an invitation's acceptance/decline (by the other party) shows up without a manual refresh (TECH_STACK §49: refetch, not WebSockets). */
const INVITATIONS_POLL_MS = 10_000;

export function useIncomingInvitations(): UseQueryResult<Invitation[]> {
  return useQuery({
    queryKey: INVITATIONS_INCOMING_QUERY_KEY,
    queryFn: () => apiClient.invitations.listIncoming(),
    refetchInterval: INVITATIONS_POLL_MS,
  });
}

export function useOutgoingInvitations(): UseQueryResult<Invitation[]> {
  return useQuery({
    queryKey: INVITATIONS_OUTGOING_QUERY_KEY,
    queryFn: () => apiClient.invitations.listOutgoing(),
    refetchInterval: INVITATIONS_POLL_MS,
  });
}

/**
 * Accepting seeds the new session's query cache, mirroring
 * `useCreateSoloSession` (`lib/games.js`), so `/play/:sessionId` never
 * refetches on first paint, and invalidates the session lists — the accepted
 * game is now one of the player's active games.
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => apiClient.invitations.accept(invitationId),
    onSuccess: (session: GameSession) => {
      queryClient.setQueryData(gameSessionQueryKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_INCOMING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

export function useDeclineInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => apiClient.invitations.decline(invitationId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_INCOMING_QUERY_KEY }),
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => apiClient.invitations.cancel(invitationId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_OUTGOING_QUERY_KEY }),
  });
}
